import request from './request'
import {
  buildLegendItems,
  type ScheduleLegendItem,
  type ScheduleLegendMeta,
  type ScheduleTechnicianView,
} from './ScheduleMeta'
import { getMockScheduleBoardResponse, shouldUseScheduleMock } from './scheduleMetaMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface ScheduleBoardParams {
  date: string
  /**
   * 类别筛选（以后端为准）：
   * - `'all'`：不限
   * - 单个 string：只筛该类别
   * - string[]：多选 OR
   */
  category: string | string[]
  /** 与 category 同步的数组形式，便于后端统一按列表解析 */
  categories: string[]
  technician_id: number
}

export interface ScheduleBoardTechnician {
  id: number
  name: string
  role?: string
  role_text?: string
  staff_no?: string
  blocks?: ScheduleBoardBlock[]
  [key: string]: unknown
}

export interface ScheduleBoardBlock {
  source_type?: string
  source_id?: number | string
  technician_id?: number | string
  category?: string
  category_text?: string
  color_key?: string
  time_start?: string
  time_end?: string
  start_time?: string
  end_time?: string
  address?: string
  full_address?: string
  tel_no?: string
  [key: string]: unknown
}

export interface ScheduleBoardData {
  date?: string
  date_text?: string
  technicians?: ScheduleBoardTechnician[]
  blocks?: ScheduleBoardBlock[]
  schedule_blocks?: ScheduleBoardBlock[]
  list?: ScheduleBoardBlock[]
  legend?: ScheduleLegendMeta[]
  [key: string]: unknown
}

export interface ScheduleAppointmentSource {
  id: string
  technicianId: string
  category: string
  title: string
  address: string
  startTime: string
  endTime: string
}

export interface ScheduleBoardView {
  date: string
  dateText: string
  technicians: ScheduleTechnicianView[]
  appointments: ScheduleAppointmentSource[]
  legend: ScheduleLegendItem[]
}

/**
 * 组装传给 board 的 categories（前端只传参，过滤由后端完成）：
 * - 未选 / 含 all → `['all']`
 * - 否则去重后的具体 value 列表
 */
export function resolveBoardCategories(categories: string[]): string[] {
  if (!categories.length || categories.includes('all')) return ['all']
  const selected = [
    ...new Set(
      categories
        .filter((value) => value && value !== 'all')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ]
  return selected.length ? selected : ['all']
}

function resolveTechnicianId(technician: string): number {
  if (!technician || technician === 'all') return 0
  const parsed = Number(technician)
  return Number.isNaN(parsed) ? 0 : parsed
}

function readBlockField(item: ScheduleBoardBlock, ...keys: string[]): string {
  const row = item as Record<string, unknown>
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return ''
}

function readBlockTechnicianId(item: ScheduleBoardBlock): string | null {
  const raw = readBlockField(
    item,
    'technician_id',
    'technicianId',
    'staff_id',
    'staffId',
    'inspector_id',
    'inspectorId',
  )
  return raw || null
}

function formatTwentyFourHour(hour: number, minute: string): string {
  return `${String(hour).padStart(2, '0')}:${minute.padStart(2, '0')}`
}

/** Normalize backend time strings to `HH:mm` for grid mapping. */
export function normalizeScheduleTime(value?: string): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''

  const datetimeTail = raw.match(/[T\s](\d{1,2}):(\d{2})(?::\d{2})?(?:\s*(AM|PM))?/i)
  if (datetimeTail?.[1] && datetimeTail[2]) {
    let hour = Number(datetimeTail[1])
    const minute = datetimeTail[2]
    const period = datetimeTail[4]?.toUpperCase()
    if (period === 'PM' && hour < 12) hour += 12
    if (period === 'AM' && hour === 12) hour = 0
    return formatTwentyFourHour(hour, minute)
  }

  const ampm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i)
  if (ampm?.[1] && ampm[2] && ampm[3]) {
    let hour = Number(ampm[1])
    const minute = ampm[2]
    const period = ampm[3].toUpperCase()
    if (period === 'PM' && hour < 12) hour += 12
    if (period === 'AM' && hour === 12) hour = 0
    return formatTwentyFourHour(hour, minute)
  }

  const compactAmpm = raw.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i)
  if (compactAmpm?.[1] && compactAmpm[2] && compactAmpm[3]) {
    let hour = Number(compactAmpm[1])
    const minute = compactAmpm[2]
    const period = compactAmpm[3].toUpperCase()
    if (period === 'PM' && hour < 12) hour += 12
    if (period === 'AM' && hour === 12) hour = 0
    return formatTwentyFourHour(hour, minute)
  }

  const twentyFour = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (twentyFour?.[1] && twentyFour[2]) {
    return formatTwentyFourHour(Number(twentyFour[1]), twentyFour[2])
  }

  return raw
}

function extractBoardBlocks(data: ScheduleBoardData | null | undefined): ScheduleBoardBlock[] {
  if (!data) return []

  const topLevel = [data.blocks, data.schedule_blocks, data.list].find((value) => Array.isArray(value))
  if (Array.isArray(topLevel) && topLevel.length) {
    return topLevel as ScheduleBoardBlock[]
  }

  const nested: ScheduleBoardBlock[] = []
  for (const technician of data.technicians ?? []) {
    const technicianBlocks = technician.blocks
    if (!Array.isArray(technicianBlocks)) continue

    for (const block of technicianBlocks) {
      nested.push({
        ...block,
        technician_id: block.technician_id ?? technician.id,
      })
    }
  }

  return nested
}

export function buildScheduleBoardParams(input: {
  date: string
  categories: string[]
  technician: string
}): ScheduleBoardParams {
  const categories = resolveBoardCategories(input.categories)
  const isAll = categories.length === 1 && categories[0] === 'all'
  // 单选 → string；多选 → 原样数组（不再误传 category:"all"）
  const category: string | string[] = isAll
    ? 'all'
    : categories.length === 1
      ? (categories[0] ?? 'all')
      : categories

  return {
    date: input.date.trim(),
    category,
    categories,
    technician_id: resolveTechnicianId(input.technician),
  }
}

export function mapScheduleBoardTechnician(item: ScheduleBoardTechnician): ScheduleTechnicianView {
  return {
    id: String(item.id),
    name: item.name?.trim() ?? '',
    role: item.role_text?.trim() || item.role?.trim() || '',
  }
}

export function mapScheduleBoardBlock(item: ScheduleBoardBlock): ScheduleAppointmentSource | null {
  const technicianId = readBlockTechnicianId(item)
  if (!technicianId) return null

  const sourceType = readBlockField(item, 'source_type', 'sourceType') || 'block'
  const sourceId = readBlockField(item, 'source_id', 'sourceId', 'id')
  const startTime = normalizeScheduleTime(
    readBlockField(item, 'time_start', 'timeStart', 'start_time', 'startTime'),
  )
  const endTime = normalizeScheduleTime(
    readBlockField(item, 'time_end', 'timeEnd', 'end_time', 'endTime'),
  )
  if (!startTime || !endTime) return null

  return {
    id: sourceId ? `${sourceType}-${sourceId}` : `${sourceType}-${technicianId}-${startTime}`,
    technicianId,
    category:
      readBlockField(item, 'category', 'color_key', 'colorKey') ||
      readBlockField(item, 'plan_key', 'planKey'),
    title:
      readBlockField(item, 'category_text', 'categoryText', 'title', 'name') ||
      readBlockField(item, 'category'),
    address:
      readBlockField(item, 'address') ||
      readBlockField(item, 'full_address', 'fullAddress'),
    startTime,
    endTime,
  }
}

export function mapScheduleBoardToView(data: ScheduleBoardData | null | undefined): ScheduleBoardView {
  const technicians = (data?.technicians ?? [])
    .map(mapScheduleBoardTechnician)
    .filter((item) => item.id && item.name)

  const appointments = extractBoardBlocks(data)
    .map(mapScheduleBoardBlock)
    .filter((item): item is ScheduleAppointmentSource => item != null)

  return {
    date: data?.date?.trim() ?? '',
    dateText: data?.date_text?.trim() ?? '',
    technicians,
    appointments,
    legend: buildLegendItems(data?.legend),
  }
}

/** POST `/api/admin/schedule/board` */
export async function postScheduleBoard(
  params: ScheduleBoardParams,
): Promise<ApiResponse<ScheduleBoardData>> {
  if (shouldUseScheduleMock()) {
    return getMockScheduleBoardResponse(params) as ApiResponse<ScheduleBoardData>
  }
  return request.post<ApiResponse<ScheduleBoardData>>('/api/admin/schedule/board', params)
}
